import React, { useCallback, useMemo, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const t = (text) => {
    if (typeof window !== 'undefined' && typeof window.__ === 'function') {
        try {
            return window.__(text);
        } catch (error) {
            return text;
        }
    }

    return text;
};

const resolveEndpoint = (template, id) => (template || '').replace('__GATE_ID__', String(id));

const parseTriggerTimeoutToSeconds = (value) => {
    const rawValue = String(value ?? '').trim();
    if (rawValue === '') {
        return null;
    }

    if (rawValue.includes(':')) {
        const parts = rawValue.split(':').map((part) => part.trim());
        if (parts.some((part) => part === '' || !/^\d+$/.test(part))) {
            return null;
        }

        const numericParts = parts.map((part) => Number.parseInt(part, 10));
        if (numericParts.some((part) => !Number.isFinite(part) || part < 0)) {
            return null;
        }

        if (numericParts.length === 3) {
            return (numericParts[0] * 3600) + (numericParts[1] * 60) + numericParts[2];
        }

        if (numericParts.length === 2) {
            return (numericParts[0] * 60) + numericParts[1];
        }

        return null;
    }

    const numericValue = Number(rawValue);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
        return null;
    }

    return numericValue;
};

const normalizeRelayConfigs = (gate) => {
    const relayLabels = gate?.relay_labels && typeof gate.relay_labels === 'object' ? gate.relay_labels : {};

    if (!Array.isArray(gate?.relays)) {
        return [];
    }

    return gate.relays
        .map((entry) => {
            if (!entry || typeof entry !== 'object') {
                return null;
            }

            const channel = Number(entry.index ?? entry.channel ?? entry.id);
            const timeout = Number(entry.timeout ?? 2);
            if (!Number.isFinite(channel) || channel <= 0) {
                return null;
            }

            const relayMeta = relayLabels[channel] || {};
            const label = String(entry.label ?? relayMeta.label ?? '').trim();
            const description = String(entry.description ?? relayMeta.description ?? '').trim();

            return {
                tip: ['out', 'in'].includes(String(entry.tip ?? entry.type ?? 'out')) ? String(entry.tip ?? entry.type ?? 'out') : 'out',
                action: ['open', 'close'].includes(String(entry.action ?? 'open')) ? String(entry.action ?? 'open') : 'open',
                index: channel,
                timeout: Number.isFinite(timeout) && timeout >= 0 ? Number(timeout.toFixed(2)) : 2,
                label: label !== '' ? label : `Relay ${channel}`,
                description,
            };
        })
        .filter(Boolean)
        .filter((entry) => entry.tip === 'out')
        .sort((left, right) => left.index - right.index);
};

const buildGateControls = (gates, triggerUrlTemplate) => {
    if (!Array.isArray(gates)) {
        return [];
    }

    return gates
        .map((gate) => {
            const relays = normalizeRelayConfigs(gate);
            const triggerUrl = gate?.trigger_url || resolveEndpoint(triggerUrlTemplate, gate?.id ?? '');

            return {
                ...gate,
                trigger_url: triggerUrl,
                relays,
                default_timeout: relays.find((entry) => entry.action === 'open')?.timeout ?? relays[0]?.timeout ?? 2,
            };
        })
        .filter((gate) => gate.trigger_url);
};

/**
 * Render reusable manual gate trigger controls and the shared custom-time dialog.
 */
export default function GateTriggerControls({
    gates = [],
    triggerUrlTemplate = '',
    csrfToken = '',
    showGateName = true,
    emptyText = 'Assigned from device page',
    auditContext = null,
    onSuccess = null,
    onError = null,
}) {
    const gateControls = useMemo(() => buildGateControls(gates, triggerUrlTemplate), [gates, triggerUrlTemplate]);
    const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
    const [triggerSubmitting, setTriggerSubmitting] = useState(false);
    const [triggerSelection, setTriggerSelection] = useState([]);
    const [triggerTimeout, setTriggerTimeout] = useState('2');
    const [triggerGate, setTriggerGate] = useState(null);
    const [triggerError, setTriggerError] = useState('');
    const [activeRequestKey, setActiveRequestKey] = useState('');
    const [successRequestKey, setSuccessRequestKey] = useState('');

    const apiFetch = useCallback(async (url, payload) => {
        const response = await fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                ...(csrfToken ? { 'X-CSRF-TOKEN': csrfToken } : {}),
            },
            body: JSON.stringify(payload ?? {}),
        });

        let data = {};
        try {
            data = await response.json();
        } catch (error) {
            data = {};
        }

        if (!response.ok) {
            const requestError = new Error(data.message || t('Failed to trigger relay.'));
            requestError.payload = data;
            throw requestError;
        }

        return data;
    }, [csrfToken]);

    const flashSuccess = useCallback((requestKey) => {
        setSuccessRequestKey(requestKey);
        window.setTimeout(() => {
            setSuccessRequestKey((current) => (current === requestKey ? '' : current));
        }, 1500);
    }, []);

    const handleRequestSuccess = useCallback((payload, fallbackMessage, requestKey) => {
        flashSuccess(requestKey);
        if (typeof onSuccess === 'function') {
            onSuccess(payload?.message || fallbackMessage);
        }
    }, [flashSuccess, onSuccess]);

    const handleRequestError = useCallback((error, fallbackMessage) => {
        if (typeof onError === 'function') {
            onError(error?.message || fallbackMessage, error);
            return;
        }

        window.alert(error?.message || fallbackMessage);
    }, [onError]);

    const openTriggerDialog = useCallback((gate) => {
        const relayIndexes = gate.relays.map((entry) => entry.index);
        setTriggerGate(gate);
        setTriggerSelection(relayIndexes.length === 1 ? relayIndexes : []);
        setTriggerTimeout(String(gate.default_timeout ?? 2));
        setTriggerError('');
        setTriggerDialogOpen(true);
    }, []);

    const sendDirectTrigger = useCallback(async (gate, relay) => {
        const requestKey = `direct-${gate.id}-${relay.index}`;
        setActiveRequestKey(requestKey);

        try {
            const payload = await apiFetch(gate.trigger_url, { index: relay.index, audit_context: auditContext });
            handleRequestSuccess(payload, t('Command sent successfully.'), requestKey);
        } catch (error) {
            handleRequestError(error, t('Failed to trigger relay.'));
        } finally {
            setActiveRequestKey((current) => (current === requestKey ? '' : current));
        }
    }, [apiFetch, handleRequestError, handleRequestSuccess]);

    const submitTriggerDialog = useCallback(async () => {
        if (!triggerGate) {
            return;
        }

        if (triggerSelection.length === 0) {
            setTriggerError(t('Select at least one relay.'));
            return;
        }

        const timeoutSeconds = parseTriggerTimeoutToSeconds(triggerTimeout);
        if (timeoutSeconds === null) {
            setTriggerError(t('Use seconds or h:m:s. Examples: 30, 90, 00:02:30, 02:00:00.'));
            return;
        }

        const requestKey = `custom-${triggerGate.id}`;
        setTriggerError('');
        setTriggerSubmitting(true);
        setActiveRequestKey(requestKey);

        try {
            const payload = await apiFetch(triggerGate.trigger_url, {
                indexes: triggerSelection,
                timeout: timeoutSeconds,
                audit_context: auditContext,
            });
            setTriggerDialogOpen(false);
            handleRequestSuccess(payload, t('Command sent successfully.'), requestKey);
        } catch (error) {
            setTriggerError(error?.message || t('Failed to trigger relay.'));
        } finally {
            setTriggerSubmitting(false);
            setActiveRequestKey((current) => (current === requestKey ? '' : current));
        }
    }, [apiFetch, auditContext, handleRequestSuccess, triggerGate, triggerSelection, triggerTimeout]);

    if (gateControls.length === 0) {
        return <Typography variant="caption" color="text.secondary">{t(emptyText)}</Typography>;
    }

    return (
        <>
            <Stack spacing={1}>
                {gateControls.map((gate) => (
                    <Stack key={`gate-trigger-${gate.id}`} spacing={0.75}>
                        {showGateName && (
                            <Typography variant="caption" sx={{ color: '#1a73e8', fontWeight: 600 }}>
                                {gate.name}
                            </Typography>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                                size="small"
                                variant={successRequestKey === `custom-${gate.id}` ? 'contained' : 'contained'}
                                color={successRequestKey === `custom-${gate.id}` ? 'success' : 'primary'}
                                startIcon={<PlayArrowIcon />}
                                onClick={() => openTriggerDialog(gate)}
                                disabled={triggerSubmitting || activeRequestKey === `custom-${gate.id}`}
                            >
                                {t('Custom time')}
                            </Button>
                            {gate.relays.map((relay) => {
                                const requestKey = `direct-${gate.id}-${relay.index}`;
                                const directButton = (
                                    <Button
                                        key={requestKey}
                                        size="small"
                                        variant={successRequestKey === requestKey ? 'contained' : 'outlined'}
                                        color={successRequestKey === requestKey ? 'success' : 'primary'}
                                        startIcon={<PlayArrowIcon />}
                                        onClick={() => sendDirectTrigger(gate, relay)}
                                        disabled={activeRequestKey === requestKey}
                                    >
                                        {relay.label}
                                    </Button>
                                );

                                if (!relay.description) {
                                    return directButton;
                                }

                                return (
                                    <Tooltip key={requestKey} title={relay.description} arrow>
                                        <span>{directButton}</span>
                                    </Tooltip>
                                );
                            })}
                        </Stack>
                    </Stack>
                ))}
            </Stack>

            <Dialog open={triggerDialogOpen} onClose={() => !triggerSubmitting && setTriggerDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{t('Custom time')}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {triggerGate
                                ? `${t('Select the relays you want to trigger for')} ${triggerGate.name}. ${t('The custom time overrides the configured timeout only for open relays.')}`
                                : t('Select the relays you want to trigger.')}
                        </Typography>

                        {triggerGate?.relays.map((relay) => {
                            const checked = triggerSelection.includes(relay.index);
                            return (
                                <Button
                                    key={`trigger-relay-${triggerGate.id}-${relay.index}`}
                                    variant={checked ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setTriggerSelection((current) => (
                                            current.includes(relay.index)
                                                ? current.filter((entry) => entry !== relay.index)
                                                : [...current, relay.index].sort((left, right) => left - right)
                                        ));
                                    }}
                                >
                                    {`${relay.label} | ${relay.tip} | ${relay.action} | ${relay.timeout}s`}
                                </Button>
                            );
                        })}

                        <TextField
                            label={t('Custom trigger time (seconds or h:m:s)')}
                            type="text"
                            value={triggerTimeout}
                            onChange={(event) => setTriggerTimeout(event.target.value)}
                            error={triggerError !== ''}
                            helperText={triggerError || t('Examples: 30, 90, 00:02:30, 02:00:00.')}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTriggerDialogOpen(false)} disabled={triggerSubmitting}>{t('Cancel')}</Button>
                    <Button variant="contained" onClick={submitTriggerDialog} disabled={triggerSubmitting}>{t('Send')}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}